import { Container, FederatedPointerEvent, Graphics, Point, type PointData } from 'pixi.js';
import { DropShadowFilter } from 'pixi-filters';
import { Canvas } from './Canvas.class';
import { CANVAS_LABEL } from '@/constants';
import { PixiGuideLine } from './guideline/PixiGuideLine.class';
import { Element } from './element/Element.class';
import { TextElement } from './element/TextElement.class';
import { PixiTransformer, DEFAULT_ANCHORS } from './transformer/PixiTransformer.class';
import type { BookPage } from './book/BookPage.class';
import type { Action } from '../timeline/action';
import { PixiGlobal } from './PixiGlobal';

interface DragState {
  startPos: { x: number; y: number };
  startMousePos: { x: number; y: number };
}

type ElementCallBackType = (element: Element | null) => void;

export class EditorCanvas extends Canvas {
  private guideLine: PixiGuideLine | null = null;
  private transformer: PixiTransformer | null = null;

  private currentPage: BookPage | null = null;
  private currentElement: Element | null = null;
  private shadowFilter: DropShadowFilter | null = null;
  private editorMask: Graphics | null = null;

  // 拖动状态：dragState 为 null 表示未拖动
  private dragState: DragState | null = null;

  // 双击检测：记录上次点击的时间和目标
  private lastClickTime: number = 0;
  private lastClickTarget: Element | null = null;
  private static readonly DOUBLE_CLICK_THRESHOLD = 300; // ms

  public onSelect: ElementCallBackType | null = null;
  public onElementUpdate: ElementCallBackType | null = null;

  protected initOthers(): void {
    if (this.app) PixiGlobal.init(this.app);

    this.initGuideLine();
    this.initTransformer();
    this.initEditorMask();
  }

  private initGuideLine(): void {
    this.guideLine = new PixiGuideLine();
    this.guideLine.updateCheckLabel(CANVAS_LABEL.ELEMENT);
  }

  private initTransformer(): void {
    this.transformer = new PixiTransformer({ keepRatio: true });

    // 文本元素 resize 约束：禁止翻转 + 最小宽度限制
    // 避免 box 宽度低于文本最小渲染宽度后 resize 矩阵增量出现位移偏差
    this.transformer.options.boundBoxFunc = (oldBox, newBox) => {
      if (this.currentElement instanceof TextElement) {
        const minW = this.currentElement.minTextWidth + this.transformer!.options.padding * 2;
        const oldSign = Math.sign(oldBox.width || 1);

        // 禁止翻转：宽度符号发生变化（锚点拖过对边）时，钳在最小宽度
        const flipping = Math.sign(newBox.width || 1) !== oldSign;
        const tooNarrow = Math.abs(newBox.width) < minW;

        if (flipping || tooNarrow) {
          const clampedWidth = minW * oldSign;

          // 左边锚点拖拽时 newBox.x 会移动，需固定右边缘以防元素被拖偏
          if (Math.abs(newBox.x - oldBox.x) > Math.abs(newBox.y - oldBox.y)) {
            const rightEdge = oldBox.x + oldBox.width;
            return { ...newBox, width: clampedWidth, x: rightEdge - clampedWidth };
          }

          // 右边锚点拖拽：左边缘不动，仅钳宽度
          return { ...newBox, width: clampedWidth };
        }
      }
      return newBox;
    };
  }

  private initEditorMask(): void {
    this.editorMask = new Graphics({ label: 'editor-mask' });
    this.editorMask.eventMode = 'none'; // 不拦截事件，允许穿透到下层元素
    this.stage.addChild(this.editorMask);
    this.updateEditorMask();
  }

  private updateEditorMask(): void {
    if (!this.editorMask || !this.app) return;

    const { screen } = this.app;
    const mask = this.editorMask;
    mask.clear();

    if (!this.currentPage) {
      // 无当前页时不显示遮罩
      return;
    }

    const page = this.currentPage;
    const pageW = page.pageWidth * page.scale.x;
    const pageH = page.pageHeight * page.scale.y;
    const x = -pageW / 2;
    const y = -pageH / 2;

    // 全屏半透明遮罩，镂空 page 区域（v8 API: fill 后用 cut() 打洞）
    mask
      .rect(-screen.width / 2, -screen.height / 2, screen.width, screen.height)
      .fill({ color: 0xa5a5a5, alpha: 0.2 })
      .rect(x, y, pageW, pageH)
      .cut();

    // 镂空边界虚线描边（PixiJS v8 无原生 dash，手动分段绘制）
    const dashLen = 3;
    const gapLen = 3;
    const td = dashLen + gapLen;

    // 上边
    for (let i = 0; i < pageW; i += td) {
      mask.moveTo(x + i, y).lineTo(Math.min(x + i + dashLen, x + pageW), y);
    }
    // 下边
    for (let i = 0; i < pageW; i += td) {
      mask.moveTo(x + i, y + pageH).lineTo(Math.min(x + i + dashLen, x + pageW), y + pageH);
    }
    // 左边
    for (let i = 0; i < pageH; i += td) {
      mask.moveTo(x, y + i).lineTo(x, Math.min(y + i + dashLen, y + pageH));
    }
    // 右边
    for (let i = 0; i < pageH; i += td) {
      mask.moveTo(x + pageW, y + i).lineTo(x + pageW, Math.min(y + i + dashLen, y + pageH));
    }
    mask.stroke({ width: 1, color: 0x000000, alpha: 0.6 });
  }

  protected initEvents(): void {
    super.initEvents();

    this.transformer!.options.onChange = (_targets, mode) => {
      if (!this.currentElement) return;

      if (this.currentElement instanceof TextElement === false || mode === 'rotate') return;

      const visualWidth = this.currentElement.originWidth * this.currentElement.scale.x;
      this.currentElement.resizeWordWrap(visualWidth);
      this.transformer?.update(); // 文本重排后 bounds 已变，刷新变形框使其贴合新尺寸
      this.transformer?.refreshDragState(); // 重置拖拽快照，避免下一帧基于过时状态计算增量
    };

    /**
     * 拖拽结束后统一同步数据
     */
    this.transformer!.options.onTransformEnd = () => {
      if (!this.currentElement) return;

      // 文本元素：拖拽结束后根据文本实际渲染尺寸修正元素宽高，
      // 避免短单行文本被拖宽后元素保持过宽的 wordWrapWidth
      //（先注释这个逻辑，云展网的编辑器的文本也是当前的逻辑）
      // if (this.currentElement instanceof TextElement) {
      //   this.currentElement.fitToTextContent();
      //   // fitToTextContent 修改了元素尺寸，需刷新 transformer 使其贴合新尺寸
      //   this.transformer?.update();
      // }

      this.currentElement.syncAttrsFromView();
      this.onElementUpdate?.(this.currentElement);
    };

    this.stage.on('pointerdown', this.onStageDown);
  }

  /**
   * 双击检测：同一 TextElement 在阈值内连续点击两次 → 进入编辑模式。
   * @returns true 表示本次点击被识别为双击并已处理，调用方应直接 return
   */
  private tryDoubleClick(target: unknown): boolean {
    const now = Date.now();

    if (
      target instanceof TextElement &&
      target === this.lastClickTarget &&
      now - this.lastClickTime <= EditorCanvas.DOUBLE_CLICK_THRESHOLD
    ) {
      // 进入编辑模式前隐藏变形框
      if (this.transformer?.visible) {
        this.transformer.detach();
      }

      target.startEditing(() => {
        target.isEditing = false;
        if (this.transformer && this.currentElement === target) {
          this.elementContainer?.addChild(this.transformer);
          this.transformer.nodes([target]);
        }
        this.onElementUpdate?.(target);
      });

      this.lastClickTime = 0;
      this.lastClickTarget = null;
      return true;
    }

    this.lastClickTime = now;
    this.lastClickTarget = target instanceof Element ? target : null;
    return false;
  }

  private onStageDown = (event: FederatedPointerEvent) => {
    const target = event.target;

    if (this.tryDoubleClick(target)) return;

    if (target instanceof Element == false) {
      this.currentElement = null;
      if (this.transformer) {
        if (this.transformer.parent) this.transformer.parent.removeChild(this.transformer);

        if (this.transformer.visible) this.transformer.detach();
      }

      this.onSelect?.(null);
      return;
    }

    this.currentElement = target;

    // 必须先把 transformer 挂到容器上，再绑定目标：
    // PixiTransformer.redraw 依赖 transformer 当前的父级链，把「世界坐标 box」
    // 换算回自身局部坐标再绘制。若先 nodes（触发首次 redraw）再 addChild，
    // 首帧会因父级缺失/错误而导致变形框与元素错位。
    if (this.transformer) {
      this.elementContainer?.addChild(this.transformer);

      // 文本元素只显示左右锚点（横向换行控制），其他元素显示全部锚点
      this.transformer.options.enabledAnchors =
        target instanceof TextElement ? ['middle-left', 'middle-right'] : DEFAULT_ANCHORS;
    }
    this.transformer?.nodes([target]);

    // 记录按下点相对于精灵父级的偏移，避免拖动开始时精灵"跳"到指针位置
    const local = this.convertToLocal(event.global, target.parent);

    this.dragState = {
      startPos: { x: target.x, y: target.y },
      startMousePos: { x: local.x, y: local.y }
    };

    if (this.currentPage) {
      // this.guideLine?.updateStaticLineStops(this.currentPage);
      this.guideLine?.updateStaticLineStopsBySize(
        this.currentPage.pageWidth,
        this.currentPage.pageHeight
      );
      this.guideLine?.start(target);
    }

    this.onSelect?.(this.currentElement);
    this.addDragListeners();
  };

  private addDragListeners(): void {
    this.stage.on('globalpointermove', this.onStageMove);
    this.stage.on('pointerup', this.onStageUp);
    this.stage.on('pointerupoutside', this.onStageUp);
    this.stage.on('globalpointerup', this.onStageUp);
    this.stage.on('globalpointerupoutside', this.onStageUp);
  }

  private removeDragListeners(): void {
    this.stage.off('globalpointermove', this.onStageMove);
    this.stage.off('pointerup', this.onStageUp);
    this.stage.off('pointerupoutside', this.onStageUp);
    this.stage.off('globalpointerup', this.onStageUp);
    this.stage.off('globalpointerupoutside', this.onStageUp);
  }

  private onStageMove = (event: FederatedPointerEvent) => {
    if (!this.dragState || !this.currentElement) return;

    const { startPos, startMousePos } = this.dragState;

    // 在精灵父级的坐标系中移动，保持抓取点不变
    const local = this.convertToLocal(event.global, this.currentElement.parent);
    this.currentElement.x = startPos.x + (local.x - startMousePos.x);
    this.currentElement.y = startPos.y + (local.y - startMousePos.y);

    if (this.currentElement) this.guideLine?.listen(this.currentElement);

    this.transformer?.update();
  };

  private onStageUp = () => {
    if (!this.dragState || !this.currentElement) return;

    this.currentElement.syncAttrsFromView();
    this.onElementUpdate?.(this.currentElement);

    this.dragState = null;

    this.guideLine?.end();

    this.removeDragListeners();
  };

  public setCurrentPage(id: string) {
    if (this.currentPage?.pageId === id) return;

    if (this.transformer?.visible) this.transformer.detach();

    if (this.currentPage) {
      if (this.currentPage.parent) this.currentPage.parent.removeChild(this.currentPage);
      this.currentPage.filters = null;
      this.currentPage.setMaskEnabled(true); // 恢复旧页的裁剪遮罩
    }

    this.currentPage = this.getPage(id);
    if (this.currentPage) {
      this.elementContainer.addChild(this.currentPage);
      this.fitPageToCanvas(this.currentPage);
      this.currentPage.setMaskEnabled(false); // 编辑器中关闭裁剪遮罩，允许元素超界显示
      // this.currentPage.filters = [this.getShadowFilter()];
    }
    this.updateEditorMask();
  }

  /**
   * 删除页时同步清理编辑器选中态：若删除的是当前页，先摘下变形框并清空
   * currentPage / currentElement，避免父类销毁页面后，watch 触发的 setCurrentPage
   * 再访问已销毁对象。
   */
  public override removePage(id: string): void {
    if (this.currentPage?.pageId === id) {
      if (this.transformer?.visible) this.transformer.detach();
      this.currentElement = null;
      this.currentPage = null;
    }

    super.removePage(id);
  }

  public getCurrentPage(): BookPage | null {
    return this.currentPage;
  }

  public getFirstPage(): BookPage | null {
    if (!this.pageMap) return null;

    const pages = Array.from(this.pageMap.values());

    if (pages.length === 0) return null;

    return pages[0];
  }

  /** 画布尺寸变化时重新适配当前页缩放 */
  protected override onCanvasResize(): void {
    if (this.currentPage) {
      this.fitPageToCanvas(this.currentPage);
      this.transformer?.update();
    }
    this.updateEditorMask();
  }

  public updateTransformer() {
    if (!this.transformer || !this.transformer.visible) return;
    this.transformer.update();
  }

  /**
   * 提取指定页的缩略图 canvas，供外部缩略图组件同步页面内容
   */
  public extractPageThumbnail(pageId: string): HTMLCanvasElement | null {
    const page = this.pageMap.get(pageId);
    if (!page || !this.app) return null;

    const wasMaskEnabled = page.maskEnabled;
    page.setMaskEnabled(true);

    try {
      return this.app.renderer.extract.canvas({
        target: page,
        resolution: 0.2,
        clearColor: '#ffffff'
      }) as HTMLCanvasElement;
    } finally {
      page.setMaskEnabled(wasMaskEnabled);
    }
  }

  /**
   * 向当前页面添加操作
   * @param action 要添加的操作对象
   */
  public addPageAction(action: Action) {
    if (!this.currentPage) return;

    this.currentPage.addAction(action);
  }

  // private getShadowFilter(): DropShadowFilter {
  //   if (!this.shadowFilter) {
  //     this.shadowFilter = new DropShadowFilter({ alpha: 0.2, offset: { x: 0, y: 0 } });
  //   }
  //   return this.shadowFilter;
  // }

  protected convertToLocal(point: PointData, targetContainer: Container | null = null): Point {
    return targetContainer ? targetContainer.toLocal(point) : new Point(point.x, point.y);
  }

  public override destroy(): void {
    // init 未完成时（如 onMounted 提前 return 导致路由跳转），
    // stage / transformer 等资源尚未创建，无需销毁，直接返回
    if (!this.stage) {
      super.destroy();
      return;
    }

    this.guideLine?.destroy();
    this.guideLine = null;

    if (this.transformer) {
      if (this.transformer.visible) this.transformer.detach();
      this.transformer.destroy();
      this.transformer = null;
    }

    this.stage.off('pointerdown', this.onStageDown);
    this.removeDragListeners();

    this.shadowFilter?.destroy();
    this.shadowFilter = null;

    this.editorMask?.destroy();
    this.editorMask = null;

    // 清理引用，避免 use-after-destroy
    this.currentPage = null;
    this.currentElement = null;
    this.dragState = null;
    this.onSelect = null;
    this.onElementUpdate = null;

    PixiGlobal.destroy();

    super.destroy();
  }
}
